import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '../../../core/api/client'
import { ApiError } from '../../../core/api/types'

export interface LocalRef { uuid: string; name: string }

export interface CargaProgramada {
  uuid: string
  seq: number | null
  horario: string | null
  status: string
  trip_status: string | null
  /** Já começou: carregando, em trânsito, descarregando ou encerrada. */
  travada: boolean
  escalada: boolean
}

export interface ContratoDaProgramacao {
  uuid: string
  numero: string
  nome: string
  produtor: string | null
  fazenda: LocalRef | null
  destino: LocalRef | null
}

export interface Programacao {
  uuid: string
  numero: string
  data: string
  status: string
  reprogramada: boolean
  contrato: ContratoDaProgramacao
  cargas: CargaProgramada[]
  criada_em: string | null
  atualizada_em: string | null
}

export interface ResumoDoAviso {
  programacao_uuid: string
  numero: string
  data: string | null
  cliente: string | null
  contrato: { uuid: string; numero: string; produtor: string | null; fazenda: string | null; destino: string | null }
  antes: (string | null)[] | null
  depois: (string | null)[]
  travadas: (string | null)[]
  quem: string
}

export interface AvisoDeProgramacao {
  uuid: string
  tipo: 'nova' | 'reprogramacao'
  resumo: ResumoDoAviso
  dupla: string | null
  criado_em: string | null
}

const CHAVE = ['programacoes'] as const

export function useProgramacoesDoDia(data: string) {
  return useQuery({
    queryKey: [...CHAVE, 'dia', data],
    queryFn: () => http.get<Programacao[]>('/programacoes/', { params: { data } }),
    enabled: Boolean(data),
    // O andamento da carga muda na Frente: a trava da reprogramação acompanha.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  })
}

export function useResumoDosDias(de: string, ate: string) {
  return useQuery({
    queryKey: [...CHAVE, 'resumo', de, ate],
    queryFn: () => http.get<Record<string, number>>('/programacoes/resumo', { params: { de, ate } }),
    enabled: Boolean(de && ate),
  })
}

export function useCriarProgramacao() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { contrato_uuid: string; data: string; horarios: string[] }) =>
      http.post<Programacao>('/programacoes/', body),
    onSettled: () => qc.invalidateQueries({ queryKey: CHAVE }),
  })
}

export function useReprogramar() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, horarios }: { uuid: string; horarios: string[] }) =>
      http.put<Programacao>(`/programacoes/${uuid}`, { horarios }),
    onSettled: () => qc.invalidateQueries({ queryKey: CHAVE }),
  })
}

/** O 409 da segunda programação no mesmo dia traz o identificador da existente. */
export function programacaoExistente(err: unknown): string | null {
  if (err instanceof ApiError && (err.code as string) === 'PROGRAMACAO_JA_EXISTE') {
    const det = err.details as { programacao_uuid?: string } | undefined
    return det?.programacao_uuid ?? null
  }
  return null
}

// ── Equipe ───────────────────────────────────────────────────────────────────

export function useAvisosPendentes(ativo: boolean) {
  return useQuery({
    queryKey: [...CHAVE, 'avisos'],
    queryFn: () => http.get<AvisoDeProgramacao[]>('/programacoes/avisos/pendentes'),
    enabled: ativo,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })
}

export function useDarCiente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => http.post<AvisoDeProgramacao>(`/programacoes/avisos/${uuid}/ciente`),
    onSettled: () => qc.invalidateQueries({ queryKey: [...CHAVE, 'avisos'] }),
  })
}
